import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { Agency } from '../../shared/icons';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { UnsavedChangesModal } from '../../shared/modal/UnsavedChangesModal';
import { SidebarMenu, SidebarMenuOption } from '../../shared/sidebar-menu/SidebarMenu';
import { getIsSavingAccessGroup, clearAccessGroupFilters, getCurrentAccessGroup, getAccessGroupModalOpen, getAccessGroupNameIsValid, getAccessGroupSection, getAccessGroupUpdated, selectAccessGroupSection, toggleAccessGroupModal, updateAccessGroup, AccessGroup, AccessGroupSection, saveAccessGroupStarted, AccessGroupTableName } from './store';
import { AccessGroupDocumentsSection } from './AccessGroupDocumentsSection';
import { AccessGroupEntitiesSection } from './AccessGroupEntitiesSection';
import { AccessGroupGeneralSection } from './AccessGroupGeneralSection';
import { AccessGroupUsersSection } from './AccessGroupUsersSection';
import styles from './AccessGroups.module.scss';

export const AccessGroupModal: React.FC = () => {
    const dispatch = useAppDispatch();

    const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState<boolean>(false);
    const [screenWidth, screenHeight] = useWindowResize();

    const isOpen = useAppSelector(getAccessGroupModalOpen);
    const currentAccessGroup = useAppSelector(getCurrentAccessGroup);
    const groupNameValid = useAppSelector(getAccessGroupNameIsValid);
    const groupUpdated = useAppSelector(getAccessGroupUpdated);
    const isSaving = useAppSelector(getIsSavingAccessGroup);
    const selectedSection = useAppSelector(getAccessGroupSection);

    const modalWidth = useMemo(() => screenWidth * 0.7, [screenWidth]);
    const modalHeight = useMemo(() => screenHeight * 0.7, [screenHeight]);

    const closeModal = useCallback(() => {
        if (groupUpdated) {
            setUnsavedChangesModalOpen(true);
        } else {
            dispatch(toggleAccessGroupModal());
        }
    }, [dispatch, groupUpdated]);

    const confirmAndClose = useCallback(() => {
        setUnsavedChangesModalOpen(false);
        dispatch(toggleAccessGroupModal());
    }, [dispatch]);

    const saveAccessGroup = useCallback(() => {
        dispatch(saveAccessGroupStarted());
        dispatch(toggleAccessGroupModal());
    }, [dispatch]);

    const selectSection = useCallback((sectionId: string) => dispatch(selectAccessGroupSection(sectionId as AccessGroupSection)), [dispatch]);

    const updateGroup = useCallback((key: keyof AccessGroup, value: number[] | string) => dispatch(updateAccessGroup(key, value)), [dispatch]);

    const confirmLabel = useMemo(() => `${currentAccessGroup.accessGroupId ? 'Update' : 'Create'} and Close`, [currentAccessGroup]);

    const modalTitle = currentAccessGroup.accessGroupId ? 'Update Access Group' : 'Create a New Access Group';

    const confirmDisabledTooltip = useMemo(() => {
        if (currentAccessGroup.accessGroupName === '') {
            return ['User group requires a name'];
        }
        if (!groupUpdated) {
            return ['No changes to save'];
        }
        return !groupNameValid ? ['User group name must be unique'] : [];
    }, [currentAccessGroup, groupNameValid, groupUpdated]);

    const confirmDisabled = useMemo(() => confirmDisabledTooltip.length > 0, [confirmDisabledTooltip]);

    const sections: SidebarMenuOption[] = useMemo(() => [
        { id: AccessGroupSection.GENERAL, label: AccessGroupSection.GENERAL, element: <AccessGroupGeneralSection accessGroup={currentAccessGroup} modalHeight={modalHeight} updateGroup={updateGroup} /> },
        { id: AccessGroupSection.USERS, label: AccessGroupSection.USERS, element: <AccessGroupUsersSection accessGroup={currentAccessGroup} modalHeight={modalHeight} updateGroup={updateGroup} /> },
        { id: AccessGroupSection.ENTITIES, label: AccessGroupSection.ENTITIES, element: <AccessGroupEntitiesSection accessGroup={currentAccessGroup} modalHeight={modalHeight} updateGroup={updateGroup} /> },
        { id: AccessGroupSection.DOCUMENTS, label: AccessGroupSection.DOCUMENTS, element: <AccessGroupDocumentsSection accessGroup={currentAccessGroup} modalHeight={modalHeight} updateGroup={updateGroup} /> }
    ], [currentAccessGroup, modalHeight, updateGroup]);

    useEffect(() => {
        if (!isOpen) {
            dispatch(selectAccessGroupSection(AccessGroupSection.GENERAL));
            const tables = [AccessGroupTableName.USER, AccessGroupTableName.ENTITY, AccessGroupTableName.DOCUMENT, AccessGroupTableName.DOCUMENT_GROUP];
            for (const table of tables) {
                dispatch(clearAccessGroupFilters(table));
            }
        }
    }, [dispatch, isOpen]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={closeModal}
            closeLabel='Close'
            confirmLabel={confirmLabel}
            confirmDisabled={confirmDisabled}
            confirm={saveAccessGroup}
            testId='entity'
            confirmDisabledTooltip={confirmDisabledTooltip}
            showConfirmSpinner={isSaving}
        >
            <div className={styles.accessGroupModalWrapper} style={{ width: modalWidth, height: modalHeight }} data-testid='entity-modal-wrapper'>
                <ModalHeader
                    label={modalTitle}
                    testId='access-group-modal'
                    marginBottom='0px'
                    icon={Agency}
                />
                <div className={styles.contentWrapper} data-testid='create-entity-content-wrapper'>
                    <SidebarMenu
                        sections={sections}
                        selected={selectedSection}
                        menuDisabled={false}
                        openSection={selectSection}
                        testId='company entity'
                        sidebarWidth={110}
                    />
                </div>
            </div>
            <UnsavedChangesModal isOpen={unsavedChangesModalOpen} closeModal={() => setUnsavedChangesModalOpen(false)} confirm={confirmAndClose} />
        </ConfirmationModal>
    );
};
